import type { SerializedError } from '@reduxjs/toolkit'
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import {
  type HttpErrorMessages,
  resolveHttpErrorMessage,
} from '@/features/errorHandling'
import type { EnqueueToastPayload, ToastDismissMode } from './types'

export type { HttpErrorMessages } from '@/features/errorHandling'
export {
  DEFAULT_HTTP_ERROR_MESSAGES,
  resolveHttpErrorMessage,
} from '@/features/errorHandling'

/**
 * Maps an RTK Query mutation result to an `EnqueueToastPayload`.
 * Handles HTTP error status codes with sensible defaults that can be overridden.
 * When the error status is `"PARSING_ERROR"`, falls back to `originalStatus`
 * so the real HTTP status code is used for message resolution.
 *
 * @example
 * mutation(body).then((result) => {
 *   dispatch(enqueueToast(toastForMutationResult(result, {
 *     successMessage: "Done!",
 *     dismissMode: "search-dismisses",
 *   })));
 * });
 */
export function toastForMutationResult(
  result: { data: unknown } | { error: FetchBaseQueryError | SerializedError },
  options: {
    successMessage: string
    errorMessages?: HttpErrorMessages
    dismissMode?: ToastDismissMode
  },
): EnqueueToastPayload {
  const { successMessage, errorMessages = {}, dismissMode } = options

  if ('error' in result) {
    const err = result.error as FetchBaseQueryError
    const status =
      err?.status === 'PARSING_ERROR' ? err.originalStatus : err?.status
    const message = resolveHttpErrorMessage(status, errorMessages)

    return { message, severity: 'error', dismissMode }
  }

  return { message: successMessage, severity: 'success', dismissMode }
}
